import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'

import type { MapLayer, DrawArea } from '@app/types/layerTypes'
import type { RegionOption } from '@app/components/Molecules/RegionFilter'
import type { VisibleAssets } from '@app/components/Molecules/MapView/RiskMapView/RiskMapView.utilities'
import type {
  IdentifyingInformation,
  RiskIsLoadingObject,
  RiskPerHazard,
} from '@app/domains/layers/types/layers.types'
import {
  fetchAssetExposureStats,
  fetchIdentifyingInformationAssetExposureStats,
} from '@app/domains/layers/utils/layers.utils'

interface UsePerHazardRiskProps {
  elementLayers: MapLayer[]
  hazardLayers: MapLayer[]
  regionMasks: RegionOption[] | null
  drawAreas: DrawArea[]
  visibleAssets: VisibleAssets
  hasElementFilters: boolean
  selectedIdentifyingInformation: {
    [assetType: string]: {
      [scenario: string]: IdentifyingInformation | undefined
    }
  }
  isAuthenticated: boolean
  clientName: string | null
}

export function usePerHazardRisk({
  elementLayers,
  hazardLayers,
  regionMasks,
  drawAreas,
  visibleAssets,
  hasElementFilters,
  selectedIdentifyingInformation,
  isAuthenticated,
  clientName,
}: UsePerHazardRiskProps) {
  const regions = regionMasks?.map((mask) => mask.region) ?? []

  const queryInputs = useMemo(
    () =>
      hazardLayers.flatMap((hazard) =>
        elementLayers.map((element) => ({
          element,
          hazard,
          identifyingInformationProperty:
            selectedIdentifyingInformation[element.type]?.[hazard.assetTag]?.property ?? 'default',
        })),
      ),
    [elementLayers, hazardLayers, selectedIdentifyingInformation],
  )

  const queries = useQueries({
    queries: queryInputs.map(({ element, hazard, identifyingInformationProperty }) => ({
      queryKey: [
        'risk',
        'per-hazard',
        clientName,
        element.type,
        hazard.assetTag,
        identifyingInformationProperty,
        regions,
        drawAreas,
        hasElementFilters ? visibleAssets : null,
      ],

      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const abortController = new AbortController()

        const abort = () => abortController.abort()
        signal.addEventListener('abort', abort)

        try {
          if (identifyingInformationProperty !== 'default') {
            return await fetchIdentifyingInformationAssetExposureStats(
              [element],
              [hazard],
              identifyingInformationProperty,
              regions,
              drawAreas,
              hasElementFilters ? visibleAssets : undefined,
              abortController,
            )
          }

          return await fetchAssetExposureStats(
            [element],
            [hazard],
            regions,
            drawAreas,
            hasElementFilters ? visibleAssets : undefined,
            abortController,
          )
        } finally {
          signal.removeEventListener('abort', abort)
        }
      },

      enabled: isAuthenticated,
    })),
  })

  const data = useMemo<RiskPerHazard>(() => {
    const result: RiskPerHazard = {}

    queries.forEach((query) => {
      const values = query.data ?? []

      for (const value of values) {
        if (!result[value.asset_type]) {
          result[value.asset_type] = {}
        }

        if (!result[value.asset_type][value.hazard_scenario]) {
          result[value.asset_type][value.hazard_scenario] = {}
        }

        result[value.asset_type][value.hazard_scenario][
          value.identifying_information_property ?? 'default'
        ] = value
      }
    })

    return result
  }, [queries])

  const perHazardLoading = useMemo<RiskIsLoadingObject['perHazard']>(() => {
    const loading: RiskIsLoadingObject['perHazard'] = {}

    queries.forEach((query, index) => {
      if (!query.isFetching) {
        return
      }

      const { element, hazard } = queryInputs[index]

      if (!loading[element.type]) {
        loading[element.type] = []
      }

      if (!loading[element.type].includes(hazard.assetTag)) {
        loading[element.type].push(hazard.assetTag)
      }
    })

    return loading
  }, [queries, queryInputs])

  return {
    data,
    perHazardLoading,
  }
}
